// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAccessibleSubmit from "@bg/App/Hooks/useAccessibleSubmit";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { maxLengthAttr, maxQtyValue } from "@bg/XDL/Utilities/inputValidation";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    wrapper?: InjectableCss;
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    wrapper: {
        css: css`
            display: flex;
            && button {
                width: auto;
                background: ${getColor("common.ui4")};
                padding: 0;
                margin: 0 !important;
                &:focus,
                &:active {
                    box-shadow: none !important;
                    border-color: transparent !important;
                }
                &:disabled {
                    background: ${getColor("common.ui4")} !important;
                    opacity: 0.5;
                }
            }

            button:first-child {
                padding-left: 10px;
                padding-right: 5px;
                border-radius: 4px 0 0 4px;
            }

            button:last-child {
                padding-right: 10px;
                padding-left: 10px;
                border-radius: 0 4px 4px 0;
            }

            &.qty * {
                margin-top: 0 !important;
                align-items: center;
            }
            & input {
                max-width: 67px;
                height: 40px !important;
                text-align: center;
                padding: 0 !important;
                width: auto !important;
                border-radius: 0 !important;

                @media (max-width: 900px) {
                    max-width: 100%;
                    width: 100% !important;
                }
                @media (max-width: 580px) {
                    max-width: 65px !important;
                }
            }

            & input:focus {
                border: 1px solid ${getColor("secondary.main")} !important;
            }
        `,
    },
    editableQuantityTextField: {
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 0 !important;
            `,
            formField: css`
                @media (max-width: 580px) {
                    width: auto;
                }
            `,
        },
        css: css`
            label {
                display: none;
            }
        `,
    },
    readOnlyQuantityText: {
        wrapper: {
            css: css`
                text-align: center;
            `,
        },
        heading: {
            as: "legend",
            css: css`
                display: none;
                @media (max-width: 900px) {
                    display: block;
                    text-align: center;
                    font-weight: ${BLUEPRINT_NAME === "bg" ? fontConfig.weight.bold : fontConfig.weight.semiBold};
                    line-height: ${fontConfig.lineHeight.legend};
                    margin-bottom: 4px;
                }

                @media print {
                    display: block;
                    text-align: center;
                }
            `,
        },
        text: {
            size: fontConfig.fontSize.large,
            weight: fontConfig.weight.bold,
            lineHeight: fontConfig.lineHeight.small,
            css: css`
                text-align: center;
            `,
        },
    },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
}) => {
    const localOnQtyOrderedChange = (value: string) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );

    const decreaseByOne = (event: any) => {
        let currentValue = Number.parseInt(value || "", 10);
        if (
            cartLine.properties["roundingRule"].toLowerCase() === "multiple only" &&
            parseInt(cartLine.properties["multipleSaleQty"], 10)
        ) {
            const step = parseInt(cartLine.properties["multipleSaleQty"], 10);
            currentValue = currentValue - step;
        } else {
            currentValue = currentValue - 1;
        }
        event.currentTarget.value = currentValue;
        changeHandler(event);
        onQtyOrderedChange(currentValue);
    };

    const increaseByOne = (event: any) => {
        let currentValue = Number.parseInt(value || "", 10);
        if (
            cartLine.properties["roundingRule"].toLowerCase() === "multiple only" &&
            parseInt(cartLine.properties["multipleSaleQty"], 10)
        ) {
            const step = parseInt(cartLine.properties["multipleSaleQty"], 10);
            currentValue = currentValue + step;
        } else {
            currentValue = currentValue + 1;
        }
        event.currentTarget.value = currentValue;
        changeHandler(event);
        onQtyOrderedChange(currentValue);
    };

    const [styles] = React.useState(() => mergeToNew(cartLineQuantityStyles, extendedStyles));

    if (editable && !isOutOfStock(cartLine)) {
        return (
            <StyledWrapper {...styles.wrapper} className="editable-qty">
                <Button onClick={decreaseByOne} sizeVariant="small" disabled={Number(value) === 1}>
                    <ButtonIcon src="Remove" size={10} color="secondary.main" />
                </Button>
                <TextField
                    {...styles.editableQuantityTextField}
                    type="text"
                    min={0}
                    maxLength={maxLengthAttr.qty}
                    label={label || translate(" ")}
                    value={value}
                    disabled={
                        !cart.canModifyOrder || cartLine.isPromotionItem || cart.type === "Job" || isLoading[cart.id]
                    }
                    onChange={changeHandler}
                    onKeyDown={keyDownHandler}
                    onBlur={blurHandler}
                    data-test-selector="cartline_qty"
                />
                <Button onClick={increaseByOne} sizeVariant="small" disabled={Number(value) === maxQtyValue}>
                    <ButtonIcon src="Plus" size={10} color="secondary.main" />
                </Button>
            </StyledWrapper>
        );
    }

    return (
        <SmallHeadingAndText
            extendedStyles={styles.readOnlyQuantityText}
            heading={label || translate("QTY")}
            text={cartLine.qtyOrdered!}
            data-test-selector="quoteDetail_cardLine_qty"
        />
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
